import { useCallback, useState, type PropsWithChildren } from 'react'
import { NavV3OnboardingAutoStart } from './NavV3OnboardingAutoStart'
import { NavV3OnboardingTourStep } from './NavV3OnboardingTourStep'
import { ONBOARDING_STEPS } from './NavV3OnboardingTourSteps'
import { useMarkNavV3OnboardingSeen } from './useNavV3OnboardingAccess'
import { NavV3OnboardingTourContext } from './useNavV3OnboardingTour'

// Provider for the v3 nav onboarding tour. Two entry points open the tour:
//   1. Auto-start the first time v3 becomes enabled for the browser (handled
//      by NavV3OnboardingAutoStart). Skipped if the seen-version key is set.
//   2. Manual re-trigger via the carousel slide on the insights summary page,
//      which calls `startTour()` from the context. Not throttled — clicking
//      the carousel is intentional.
//
// Lives above the nav so it survives the v3 → v2 unmount that step 1's "Use
// classic navigation instead" link triggers.
export const NavV3OnboardingTourProvider = ({ children }: PropsWithChildren<{}>) => {
  const [stepIndex, setStepIndex] = useState<number | null>(null)
  const markSeen = useMarkNavV3OnboardingSeen()

  const startTour = useCallback(() => setStepIndex(0), [])
  const close = useCallback(() => setStepIndex(null), [])
  const advance = useCallback(
    (next: number) => setStepIndex(next >= ONBOARDING_STEPS.length ? null : next),
    []
  )

  return (
    <NavV3OnboardingTourContext.Provider value={{ startTour }}>
      {children}
      <NavV3OnboardingAutoStart onAutoStart={startTour} markSeen={markSeen} />
      {stepIndex != null && (
        <NavV3OnboardingTourStep
          step={ONBOARDING_STEPS[stepIndex]}
          currentStep={stepIndex}
          totalSteps={ONBOARDING_STEPS.length}
          onStepChange={advance}
          onClose={close}
        />
      )}
    </NavV3OnboardingTourContext.Provider>
  )
}
